import React, { useEffect, useState } from 'react';
import { ceebFlagsApi } from '../services/api';
import type { CeebFlags } from '../services/api';

/**
 * Przełączniki CEEB — ODCZYT dla superadmina (raport uwag Bronka 2026-10-19, §4: decyzje G4 i G3 na spotkanie).
 *
 * Źródło: GET /api/ceeb/flags (utils/ceebFlags.js — env, potem plik flag, domyślnie WYŁĄCZONE). Zapisu z panelu
 * celowo NIE MA: flagi włączają nowe ścieżki zapisu do rejestru państwowego, a dziś przełącza je wyłącznie właściciel
 * na serwerze (`ceebFlags.js --set`, wpis audytu CEEB_FLAG_SET). Panel tylko pokazuje stan prostym językiem.
 */
type FlagKey = keyof CeebFlags;

interface FlagText {
  key: FlagKey;
  title: string;
  on: string;
  off: string;
}

// Kolejność: najpierw decyzja z G4 (data zakończenia), potem pozostałe przełączniki.
const FLAG_TEXTS: FlagText[] = [
  {
    key: 'CEEB_ACCEPT_SETS_END_DATE',
    title: 'Data zakończenia protokołu = dzień akceptacji w CEEB',
    on: 'Przy zielonym „Akceptuj” bot najpierw zmienia w CEEB „Datę zakończenia” na dzisiejszą (z kopią protokołu przed i po), a dopiero potem akceptuje.',
    off: '„Data zakończenia” to dzień ostatniej kontroli albo ostatniej zmiany wpisu w lokalu. Akceptacja jej nie zmienia.',
  },
  {
    key: 'CEEB_EDIT_ISSUED',
    title: 'Poprawianie protokołów już wprowadzonych do CEEB',
    on: 'Superadmin może poprawić nasz protokół „Wprowadzony” z tego roku — zawsze po pełnej kopii z CEEB.',
    off: 'Protokołu „Wprowadzony” bot nie poprawia. Poprawka tylko ręcznie w CEEB.',
  },
  {
    key: 'CEEB_UPDATE_ISSUED_COPIES',
    title: 'Uzupełnianie lokali skopiowanych z poprzedniej kontroli',
    on: 'Wysyłka wpisuje nasze dane z terenu także do lokali, które CEEB skopiował z poprzedniego roku jako „Wprowadzony”.',
    off: 'Lokale skopiowane z poprzedniego roku jako „Wprowadzony” bot pomija.',
  },
  {
    key: 'CEEB_SINGLE_LOCAL_ENABLED',
    title: 'Wysyłka domów jednorodzinnych (jednolokalowych)',
    on: 'Bot wysyła także budynki jednolokalowe.',
    off: 'Budynek jednolokalowy trzeba wpisać do CEEB ręcznie — wysyłka odmawia.',
  },
  {
    key: 'CEEB_OWN_CARDS_ATTACHMENT',
    title: 'Własny załącznik budynku (karty mieszkań z papieru, PDF)',
    on: 'W widoku budynku można wgrać własny PDF kart mieszkań — bot dołącza go do protokołu zamiast kart generowanych przez system.',
    off: 'Do protokołu idą zawsze karty lokali generowane przez system. Wgrywanie własnego pliku jest ukryte.',
  },
];

const box: React.CSSProperties = {
  marginTop: 'var(--space-5)',
  padding: 'var(--space-4)',
  border: 'var(--border-width) solid var(--border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--surface-card)',
};

const pill: React.CSSProperties = {
  display: 'inline-flex',
  padding: '2px var(--space-2)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-caption)',
  fontWeight: 'var(--weight-semibold)',
};
const pillOn: React.CSSProperties = { background: 'var(--status-ok-bg)', color: 'var(--status-ok)' };
const pillOff: React.CSSProperties = { background: 'var(--action-disabled-bg)', color: 'var(--text-secondary)' };

const CeebFlagsPanel: React.FC = () => {
  const [flags, setFlags] = useState<CeebFlags | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let aktualne = true;
    ceebFlagsApi.get()
      .then((f) => { if (aktualne) setFlags(f); })
      .catch((e) => { if (aktualne) setError(e instanceof Error ? e.message : 'Nie udało się odczytać przełączników CEEB'); });
    return () => { aktualne = false; };
  }, []);

  return (
    <section style={box} aria-labelledby="ceeb-flags-heading" data-testid="ceeb-flags-panel">
      <h3
        id="ceeb-flags-heading"
        style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 'var(--weight-bold)', color: 'var(--text-heading)' }}
      >
        Przełączniki bota CEEB (podgląd)
      </h3>
      <p style={{ margin: 'var(--space-2) 0 0 0', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
        Każdy przełącznik jest domyślnie wyłączony. Włącza go wyłącznie właściciel na serwerze — tutaj widać tylko,
        jak bot działa teraz.
      </p>
      {error && (
        <p role="alert" style={{ margin: 'var(--space-3) 0 0 0', color: 'var(--status-deny)' }}>{error}</p>
      )}
      {!flags && !error && (
        <p style={{ margin: 'var(--space-3) 0 0 0', color: 'var(--text-secondary)' }}>Wczytywanie…</p>
      )}
      {flags && (
        <ul style={{ listStyle: 'none', margin: 'var(--space-3) 0 0 0', padding: 0, display: 'grid', gap: 'var(--space-3)' }}>
          {FLAG_TEXTS.map((f) => {
            const on = flags[f.key] === true;
            return (
              <li key={f.key} data-testid={`ceeb-flag-${f.key}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                  <span style={{ ...pill, ...(on ? pillOn : pillOff) }}>{on ? 'Włączone' : 'Wyłączone'}</span>
                  <span style={{ fontWeight: 'var(--weight-semibold)', color: 'var(--text-primary)' }}>{f.title}</span>
                </div>
                <p style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
                  {on ? f.on : f.off}
                </p>
              </li>
            );
          })}
        </ul>
      )}
      <p style={{ margin: 'var(--space-4) 0 0 0', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
        Do decyzji: gdzie wpisywać ogólne zalecenia budynku („Przewody kominowe posiadają niewielkie uchybienia…” to
        w CEEB kropka do zaznaczenia, nie pole tekstowe). Teraz: nieprawidłowości kominowe (kody C) zaznaczają 2. albo
        3. kropkę i razem z dopiskiem budynku trafiają do „Wszelkie wskazane nieprawidłowości…”; bez kodów C bot
        zaznacza 1. kropkę, a dopisek budynku i zalecenia A trafiają do „Inne uchybienia…”.
      </p>
    </section>
  );
};

export default CeebFlagsPanel;
